import type { Region, SourceConfig } from "@gooner/shared";
import { clipLengthSummary, keywordGroups, tagLabel } from "@gooner/shared";
import { type CollectionOption } from "./monitorTemplates";

/*
 * How a region's feed is said out loud — one vocabulary for every surface that labels a
 * region tile (the editor's canvas and the screen's hover chrome), so the same source
 * never reads differently in two places.
 */

/** A short human label for a region's current feed. */
export function sourceLabel(s: SourceConfig, cols: CollectionOption[]): string {
  switch (s.kind) {
    case "random":
      return "Random mix";
    case "playlist": {
      const c = cols.find((x) => x.id === s.playlistId);
      return c ? c.name : s.playlistId ? "Collection" : "No collection yet";
    }
    case "keywords": {
      // Display names, and flattened across groups: the canvas has one line to say
      // what a region plays, and "(Latina or Asian) and Large" doesn't fit in it.
      const picked = keywordGroups(s).flat();
      return picked.length
        ? picked.map((t) => tagLabel(t)).join(", ")
        : "Keywords";
    }
    // A Community Wall square's placement — 1–5 clips a buyer picked. Not settable in
    // the editor, but a forked wall snapshot carries them and needs a name.
    case "clips":
      return s.clips.length === 1 ? "1 clip" : `${s.clips.length} clips`;
    // These three are no longer settable in the editor, but old caves still
    // carry them and the canvas has to name what a region is playing.
    case "clip":
      return "Locked clip";
    // The Keyword Scroll's feed: a stage's, never a region's — but the union is one.
    case "roulette":
      return "Keyword roulette";
    // Colorwave's feed, likewise a stage's.
    case "colorwave":
      return "Colorwave";
    // GoonTok's crowd-weighted feed — a stage's too.
    case "goontok":
      return "GoonTok";
    // Build a Slut's rounds and scroll — a stage's too.
    case "build":
      return "Build a Slut";
    case "redgifs":
      return "RedGifs";
    case "reddit":
      return "Reddit";
  }
}

/**
 * What the canvas badge says about a region's pacing, or null for no badge.
 *
 * Length IS the pacing now — a clip plays whole and the next one starts, so how long a
 * tile holds still is decided by which lengths it was filtered to. The badge reads the
 * band back. Unfiltered, a region plays the catalog's whole spread and there is nothing
 * to say — no pill, rather than an "any length" one on every tile.
 */
export function regionPacingLabel(r: Region): string | null {
  const picked = r.source.kind === "keywords" ? keywordGroups(r.source).flat() : [];
  return clipLengthSummary(picked);
}

/**
 * Whether a region still needs a feed picked — drives the canvas's accent wash.
 * "random" is the default a new region ships with and a valid choice on its own,
 * so it doesn't count as unset; only an empty collection or keyword set does.
 */
export function sourceIsUnset(s: SourceConfig): boolean {
  if (s.kind === "playlist") return !s.playlistId;
  if (s.kind === "keywords") return keywordGroups(s).length === 0;
  return false;
}
